/* Team — one page for a fleet's accounts, its people and who can do what
   (Page redesign: compact layout → Team.dc.html; ADR 0017/0018).

   A fleet is either the caller's Personal fleet (the accounts they own) or an
   Agency they co-manage. Plain-member agencies are never offered. Access, role
   and auto-placement edits are a draft until Save is pressed; structural actions
   (add/remove a person or an account, agencies) still act at once behind their
   own buttons. Tick several people and/or accounts to give them all the same
   access at once. */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  api,
  type Account,
  type AccountAssignment,
  type AgencyDetail,
  type AgencyRole,
  type AgencySummary,
  type AgencyTeamRow,
  type ManagedAccount,
  type OwnedAccount,
} from '../../../api/client';
import { useAuth } from '../../../context/AuthContext';
import { AccountAvatar } from '../../AccountAvatar';
import { Btn, ErrorLine, Icon, Loading, Seg, Spin, Toggle } from '../../ChatAssistant/compact/kit';
import { C } from '../../ChatAssistant/compact/tokens';
import { font as f } from '../../ChatAssistant/compact/style';
import { AccessEditor, AccessRow, Avatar, Check, PickBox, SearchInput } from './AccessRow';
import { initial, nameOf, useAccessSummary } from './people';
import { persistAccess, runPool } from './persistAccess';
import {
  NO_ACCESS, PRESETS, applyPreset, forPair, fromAssignment, isEmpty, sameAccess,
  type Access, type PresetId,
} from './permissionModel';
import { AVATAR_ACCEPT, prepareAvatar } from '../../../utils/avatarImage';
import './team.css';

const PERSONAL = 'personal';

export interface FleetAccount { account_id: string; name: string; owner_id: string | null; agency_name: string | null }
/** What the Add-account menu offers: an account the caller may place here. */
export interface PoolAccount { account_id: string; name: string; agency_name: string | null; pending: boolean }

const accountName = (a: Account) => a.profile_name || a.username || a.account_id;
/** Created but never connected — same rule as the sidebar's "Pending" badge. */
const isPending = (a: Account | undefined) => !!a && !a.username && !a.is_logged_in;
interface Person {
  user_id: string;
  email: string | null;
  username: string | null;
  /** Agency role; null = External; undefined = Personal fleet (roles do not exist there). */
  role?: AgencyRole | null;
  count: number;
}

const errMsg = (e: unknown, fallback: string) => (e instanceof Error && e.message) || fallback;
const matches = (q: string, ...fields: (string | null | undefined)[]) => {
  const needle = q.trim().toLowerCase();
  return !needle || fields.some((s) => s?.toLowerCase().includes(needle));
};
const pairKey = (uid: string, accountId: string) => `${uid}|${accountId}`;
const without = <T,>(rec: Record<string, T>, drop: (key: string) => boolean) =>
  Object.fromEntries(Object.entries(rec).filter(([k]) => !drop(k))) as Record<string, T>;
const toggled = (set: Set<string>, id: string) => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
};

/** Whether the caller has anything to manage in Team (else the tab is hidden). */
export function useTeamAvailability() {
  const { isSuperAdmin, isAuthenticated } = useAuth();
  const [available, setAvailable] = useState<boolean | null>(null);
  useEffect(() => {
    if (!isAuthenticated) return;
    if (isSuperAdmin) { setAvailable(true); return; }
    let live = true;
    Promise.all([api.getMyAgencies(), api.getMyOwnedAccounts(), api.getMyManagedAccounts().catch(() => [])])
      .then(([ags, owned, managed]) => { if (live) setAvailable(ags.length > 0 || owned.length > 0 || managed.length > 0); })
      .catch(() => { if (live) setAvailable(true); }); // fail open: the tab itself explains
    return () => { live = false; };
  }, [isAuthenticated, isSuperAdmin]);
  return available;
}

export function TeamTab({ onDirtyChange }: { onDirtyChange?: (dirty: boolean) => void } = {}) {
  const { t } = useTranslation('agency');
  const { user, isSuperAdmin } = useAuth();
  const summary = useAccessSummary();
  const myId = user?.id ?? '';

  const [agencies, setAgencies] = useState<AgencySummary[]>([]);
  const [owned, setOwned] = useState<OwnedAccount[]>([]);
  const [managed, setManaged] = useState<ManagedAccount[]>([]); // Permission Management, not owned
  const [visible, setVisible] = useState<Account[]>([]); // every account the caller can reach (avatars, names)
  const [fleet, setFleet] = useState<string | null>(null);
  const [detail, setDetail] = useState<AgencyDetail | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [unsaved, setUnsaved] = useState<Person | null>(null); // Personal: found, not yet granted
  const [sel, setSel] = useState<string | null>(null);
  const [assignmentsBy, setAssignmentsBy] = useState<Record<string, AccountAssignment[]>>({});
  const [loading, setLoading] = useState(true);
  const [fleetLoading, setFleetLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Unsaved edits: access per person × account, roles per person, the auto-placement switch.
  const [draft, setDraft] = useState<Record<string, Access>>({});
  const [roleDraft, setRoleDraft] = useState<Record<string, AgencyRole>>({});
  const [autoDraft, setAutoDraft] = useState<boolean | null>(null);
  const [saving, setSaving] = useState<{ done: number; total: number } | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const [placing, setPlacing] = useState(false);
  // Search and bulk selection
  const [peopleQuery, setPeopleQuery] = useState('');
  const [accountQuery, setAccountQuery] = useState('');
  const [pickedPeople, setPickedPeople] = useState<Set<string>>(new Set());
  const [pickedAccounts, setPickedAccounts] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<string | null>(null);
  const requested = useRef(new Set<string>()); // assignment loads already asked for in this fleet

  const agency = agencies.find((a) => a.id === fleet) ?? null;
  const isAgency = !!agency;
  const myRole: AgencyRole | null = agency?.role ?? null;
  const canGovern = isSuperAdmin || myRole === 'owner'; // admin tier, delete, transfer
  const fleetArg = fleet === PERSONAL ? null : fleet;

  // ---------------------------------------------------------------- loading
  const loadBase = useCallback(async () => {
    const [ags, own, man] = await Promise.all([
      api.getMyAgencies(), api.getMyOwnedAccounts(), api.getMyManagedAccounts().catch(() => []),
    ]);
    setAgencies(ags);
    setOwned(own);
    setManaged(man);
    setVisible(await api.getAccounts());
    return { ags, own: [...own, ...man] };
  }, []);

  useEffect(() => {
    let live = true;
    loadBase()
      .then(({ ags, own }) => {
        if (!live) return;
        setFleet((cur) => cur ?? (own.length > 0 || isSuperAdmin || ags.length === 0 ? PERSONAL : ags[0]!.id));
      })
      .catch((e) => live && setError(errMsg(e, t('team.loadDataFailed'))))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [loadBase, isSuperAdmin, t]);

  const loadFleet = useCallback(async (id: string) => {
    if (id === PERSONAL) {
      const team = await api.getGlobalTeam();
      setDetail(null);
      setPeople(team.map((m) => ({ user_id: m.user_id, email: m.email, username: m.username ?? null, count: m.assigned_accounts_count })));
      return;
    }
    const [d, roster] = await Promise.all([api.getAgencyDetail(id), api.getAgencyTeam(id)]);
    setDetail(d);
    setPeople(roster.map((r: AgencyTeamRow) => ({ user_id: r.user_id, email: r.email, username: r.username, role: r.agency_role, count: r.assigned_accounts_count })));
  }, []);

  useEffect(() => {
    if (!fleet) return;
    let live = true;
    setFleetLoading(true);
    setSel(null);
    setUnsaved(null);
    setAssignmentsBy({});
    requested.current = new Set();
    setPickedPeople(new Set());
    setPickedAccounts(new Set());
    setPeopleQuery('');
    setAccountQuery('');
    setNotice(null);
    setDraft({});
    setRoleDraft({});
    setAutoDraft(null);
    setJustSaved(false);
    loadFleet(fleet)
      .catch((e) => live && setError(errMsg(e, t('team.loadDataFailed'))))
      .finally(() => live && setFleetLoading(false));
    return () => { live = false; };
  }, [fleet, loadFleet, t]);

  const loadAssignments = useCallback(async (uid: string) => {
    if (!fleet) return;
    const rows = fleet === PERSONAL ? await api.getMemberAccountAssignments(uid) : await api.getAgencyMemberAccounts(fleet, uid);
    setAssignmentsBy((cur) => ({ ...cur, [uid]: rows }));
  }, [fleet]);

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    try { setError(null); await fn(); return true; } catch (e) { setError(errMsg(e, fallback)); return false; }
  };
  const refresh = async () => { await loadBase(); if (fleet) await loadFleet(fleet); };

  // ---------------------------------------------------------------- derived
  const byId = useMemo(() => new Map(visible.map((a) => [a.account_id, a])), [visible]);

  const accounts: FleetAccount[] = useMemo(() => {
    if (isAgency) return (detail?.accounts ?? []).map((a) => ({ account_id: a.account_id, name: a.name, owner_id: a.owner_id, agency_name: null }));
    if (isSuperAdmin) return visible.map((a) => ({ account_id: a.account_id, name: accountName(a), owner_id: null, agency_name: null }));
    return [
      ...owned.map((a) => ({ account_id: a.account_id, name: a.name, owner_id: myId, agency_name: a.agency_name })),
      ...managed.map((a) => ({ account_id: a.account_id, name: a.name, owner_id: null, agency_name: null })),
    ];
  }, [isAgency, detail, isSuperAdmin, visible, owned, managed, myId]);

  const roster: Person[] = useMemo(() => {
    const list = people.map((p) => (p.user_id in roleDraft ? { ...p, role: roleDraft[p.user_id] } : p));
    if (unsaved && !list.some((p) => p.user_id === unsaved.user_id)) list.unshift(unsaved);
    const rank = (p: Person) => (p.role === 'owner' ? 0 : p.role === 'admin' ? 1 : p.role === 'member' ? 2 : 3);
    return isAgency ? list.sort((a, b) => rank(a) - rank(b)) : list;
  }, [people, roleDraft, unsaved, isAgency]);

  /** Owners and admins of an agency hold every account already — nothing to grant. */
  const hasFullRole = useCallback((p: Person) => isAgency && (p.role === 'owner' || p.role === 'admin'), [isAgency]);

  const selected = roster.find((p) => p.user_id === sel) ?? roster.find((p) => p.user_id !== myId) ?? roster[0] ?? null;
  useEffect(() => { if (!sel && selected) setSel(selected.user_id); }, [sel, selected]);

  const shownPeople = roster.filter((p) => matches(peopleQuery, p.username, p.email));
  const shownAccounts = accounts.filter((a) => matches(accountQuery, a.name, byId.get(a.account_id)?.username, a.account_id));

  // Who the right pane edits: the ticked people, else the open one.
  const multi = pickedPeople.size > 0;
  const targets: Person[] = multi ? roster.filter((p) => pickedPeople.has(p.user_id)) : selected ? [selected] : [];
  const targetKey = targets.map((p) => p.user_id).join(',');
  useEffect(() => {
    for (const uid of targetKey.split(',').filter(Boolean)) {
      if (requested.current.has(uid) || unsaved?.user_id === uid) continue;
      requested.current.add(uid);
      loadAssignments(uid).catch((e) => { requested.current.delete(uid); setError(errMsg(e, t('team.loadAssignmentsFailed'))); });
    }
  }, [targetKey, unsaved, loadAssignments, t]);

  const loaded = (uid: string) => unsaved?.user_id === uid || uid in assignmentsBy;
  const savedAccessOf = (uid: string, accountId: string) => fromAssignment((assignmentsBy[uid] ?? []).find((a) => a.account_id === accountId));
  /** What the page shows and edits: the draft if there is one, else what is saved. */
  const accessOf = (uid: string, accountId: string) => draft[pairKey(uid, accountId)] ?? savedAccessOf(uid, accountId);
  const holds = (uid: string, accountId: string) => (assignmentsBy[uid] ?? []).some((a) => a.account_id === accountId);

  // A super admin may place any account; everyone else only accounts they really own.
  // Never-connected accounts are offered last and labelled.
  const pool: PoolAccount[] = useMemo(() => {
    if (!isAgency) return [];
    const inHere = new Set((detail?.accounts ?? []).map((a) => a.account_id));
    const ownedById = new Map(owned.map((o) => [o.account_id, o]));
    const source = isSuperAdmin
      ? visible.map((a) => ({ account_id: a.account_id, name: accountName(a), agency_name: ownedById.get(a.account_id)?.agency_name ?? null }))
      : owned.map((o) => ({ account_id: o.account_id, name: o.name, agency_name: o.agency_name }));
    return source
      .filter((a) => !inHere.has(a.account_id))
      .map((a) => ({ ...a, pending: isPending(byId.get(a.account_id)) }))
      .sort((x, y) => Number(x.pending) - Number(y.pending) || x.name.localeCompare(y.name));
  }, [isAgency, detail, owned, isSuperAdmin, visible, byId]);

  // ---------------------------------------------------------------- actions
  const createAgency = async (name: string) => {
    if (dirty && !confirm(t('fleet.save.discardConfirm'))) return;
    let created: { id: string } | null = null;
    const ok = await run(async () => { created = await api.createAgency(name); await loadBase(); }, t('fleet.errors.create'));
    if (ok && created) setFleet((created as { id: string }).id);
  };

  const deleteAgency = async () => {
    if (!agency || !confirm(t('fleet.confirmDelete', { name: agency.name }))) return;
    const ok = await run(async () => { await api.deleteAgency(agency.id); await loadBase(); }, t('fleet.errors.delete'));
    if (ok) setFleet(owned.length + managed.length > 0 || isSuperAdmin ? PERSONAL : (agencies.find((a) => a.id !== agency.id)?.id ?? PERSONAL));
  };

  /** Set (a picked file) or remove (null) the current agency's picture. */
  const setAgencyPicture = async (file: File | null) => {
    if (!agency) return;
    const id = agency.id;
    await run(async () => {
      let image: Blob | null = null;
      if (file) {
        try { image = await prepareAvatar(file); } catch { throw new Error(t('fleet.errors.picture')); }
      }
      const updated = image ? await api.uploadAgencyAvatar(id, image) : await api.deleteAgencyAvatar(id);
      setAgencies((cur) => cur.map((a) => (a.id === id ? { ...a, avatar_url: updated.avatar_url ?? null } : a)));
    }, t('fleet.errors.picture'));
  };

  // No bulk endpoint: place one by one so a single refusal doesn't sink the rest, refresh once.
  const placeAccounts = async (ids: string[]) => {
    if (!fleet || !ids.length) return;
    setPlacing(true);
    await run(async () => {
      let failed = 0;
      let last: unknown = null;
      for (const id of ids) {
        try { await api.placeAccountInAgency(fleet, id); } catch (e) { failed++; last = e; }
      }
      await refresh();
      if (failed === ids.length) throw last;
      if (failed) throw new Error(t('fleet.errors.placeSome', { count: failed, total: ids.length }));
    }, t('fleet.errors.place'));
    setPlacing(false);
  };

  const detach = (acc: FleetAccount, where: string) => {
    if (!confirm(t('fleet.confirmDetach', { account: acc.name, agency: where }))) return;
    void run(async () => {
      await api.detachAccountFromAgency(acc.account_id);
      if (isAgency) setDraft((cur) => without(cur, (k) => k.endsWith(`|${acc.account_id}`)));
      await refresh();
    }, t('fleet.errors.detach'));
  };

  const autoPlacement = autoDraft ?? !!agency?.auto_placement;
  const stageAutoPlacement = (on: boolean) => setAutoDraft(on === !!agency?.auto_placement ? null : on);

  const openPerson = (uid: string) => { setSel(uid); setPickedPeople(new Set()); };

  const addPerson = async (identifier: string, role: AgencyRole) => {
    if (!fleet) return;
    if (fleet === PERSONAL) {
      await run(async () => {
        const r = await api.addGlobalTeamMember(identifier);
        const known = people.find((p) => p.user_id === r.user_id);
        if (!known) setUnsaved({ user_id: r.user_id, email: r.email, username: r.username ?? null, count: 0 });
        openPerson(r.user_id);
      }, t('team.addTeamMemberFailed'));
      return;
    }
    await run(async () => {
      const r = await api.addAgencyMemberByIdentifier(fleet, identifier, role);
      await refresh();
      openPerson(r.user_id);
    }, t('team.addTeamMemberFailed'));
  };

  const removePerson = (p: Person) => {
    if (fleet === PERSONAL && unsaved?.user_id === p.user_id) { setUnsaved(null); setSel(null); return; }
    const msg = isAgency
      ? t('fleet.confirmRemove', { name: nameOf(p), agency: agency?.name, count: accounts.length })
      : t('team.removeMemberConfirm', { email: p.email });
    if (!confirm(msg)) return;
    void run(async () => {
      if (isAgency && fleet) await api.removeAgencyMember(fleet, p.user_id);
      else await api.removeGlobalTeamMember(p.user_id);
      dropPersonDrafts(p.user_id);
      setSel(null);
      await refresh();
    }, t('team.removeTeamMemberFailed'));
  };

  const dropPersonDrafts = (uid: string) => {
    setDraft((cur) => without(cur, (k) => k.startsWith(`${uid}|`)));
    setRoleDraft((cur) => without(cur, (k) => k === uid));
  };

  const stageRole = (p: Person, role: AgencyRole) => {
    const saved = people.find((x) => x.user_id === p.user_id)?.role;
    setRoleDraft((cur) => (role === saved ? without(cur, (k) => k === p.user_id) : { ...cur, [p.user_id]: role }));
    // An admin holds every account: per-account edits for them would be moot.
    if (role === 'admin') setDraft((cur) => without(cur, (k) => k.startsWith(`${p.user_id}|`)));
  };

  const makeMember = (p: Person) =>
    fleet && run(async () => { await api.addAgencyMemberByIdentifier(fleet, p.email || p.username || '', 'member'); await refresh(); }, t('team.addTeamMemberFailed'));

  const makeOwner = (p: Person) => {
    if (!agency || !confirm(t('fleet.confirmTransfer', { name: nameOf(p), agency: agency.name }))) return;
    void run(async () => { await api.transferAgencyOwnership(agency.id, p.user_id); await refresh(); }, t('fleet.errors.transfer'));
  };

  const stageAccess = (uid: string, accountId: string, next: Access) => {
    const key = pairKey(uid, accountId);
    setNotice(null);
    setDraft((cur) => (sameAccess(next, savedAccessOf(uid, accountId)) ? without(cur, (k) => k === key) : { ...cur, [key]: next }));
  };

  /** Give every ticked account the same access, for every person being edited (staged until Save). */
  const applyBulk = (template: Access) => {
    const who = targets.filter((p) => !hasFullRole(p));
    const where = accounts.filter((a) => pickedAccounts.has(a.account_id));
    const pairs = who.flatMap((p) => where.filter((a) => !(isAgency && a.owner_id === p.user_id)).map((a) => ({ uid: p.user_id, accountId: a.account_id })));
    if (!pairs.length) return;
    setDraft((cur) => {
      const next = { ...cur };
      for (const { uid, accountId } of pairs) {
        const key = pairKey(uid, accountId);
        const value = forPair(template, cur[key] ?? savedAccessOf(uid, accountId));
        if (sameAccess(value, savedAccessOf(uid, accountId))) delete next[key]; else next[key] = value;
      }
      return next;
    });
    setNotice(t('fleet.bulk.staged', { count: pairs.length }));
  };

  // ---------------------------------------------------------------- save / discard
  const dirtyCount = Object.keys(draft).length + Object.keys(roleDraft).length + (autoDraft === null ? 0 : 1);
  const dirty = dirtyCount > 0;
  useEffect(() => { if (dirty) setJustSaved(false); }, [dirty]);

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const discard = () => {
    setDraft({});
    setRoleDraft({});
    setAutoDraft(null);
    setNotice(null);
    if (unsaved) { setUnsaved(null); setSel(null); }
  };

  /** Switching fleet throws the draft away, so ask first. */
  const switchFleet = (id: string) => {
    if (id === fleet) return;
    if (dirty && !confirm(t('fleet.save.discardConfirm'))) return;
    setFleet(id);
  };

  const save = async () => {
    if (!fleet || !dirty || saving) return;
    const accessJobs = Object.entries(draft).map(([key, next]) => {
      const [uid, accountId] = key.split('|') as [string, string];
      return { key, uid, accountId, next };
    });
    const roleJobs = Object.entries(roleDraft);
    const autoJob = autoDraft;
    const total = accessJobs.length + roleJobs.length + (autoJob === null ? 0 : 1);
    let failed = 0;
    let lastError: unknown = null;
    const tick = () => setSaving((s) => s && { ...s, done: s.done + 1 });
    setError(null);
    setNotice(null);
    setJustSaved(false);
    setSaving({ done: 0, total });

    // Roles first, so a demoted admin's new per-account grants land after the demotion.
    const savedRoles: string[] = [];
    for (const [uid, role] of roleJobs) {
      try { await api.setAgencyMemberRole(fleet, uid, role); savedRoles.push(uid); } catch (e) { failed++; lastError = e; }
      tick();
    }
    let autoSaved = false;
    if (autoJob !== null && agency) {
      try { await api.setAgencyAutoPlacement(agency.id, autoJob); autoSaved = true; } catch (e) { failed++; lastError = e; }
      tick();
    }
    const savedPairs = new Set<string>();
    const errors = await runPool(accessJobs, async (j) => {
      await persistAccess(fleetArg, j.uid, j.accountId, j.next, holds(j.uid, j.accountId));
      savedPairs.add(j.key);
    }, tick);
    failed += errors.length;
    if (errors.length) lastError = errors[0];

    // Reload what was written, then drop the drafts that landed (unless edited again meanwhile).
    try {
      const touched = [...new Set(accessJobs.filter((j) => savedPairs.has(j.key)).map((j) => j.uid))];
      if (unsaved && accessJobs.some((j) => j.uid === unsaved.user_id && savedPairs.has(j.key) && !isEmpty(j.next))) setUnsaved(null);
      await Promise.all([...touched.map((uid) => loadAssignments(uid)), autoSaved ? loadBase() : null, loadFleet(fleet)]);
    } catch (e) {
      failed++; lastError = e;
    }
    const landed = new Map(accessJobs.filter((j) => savedPairs.has(j.key)).map((j) => [j.key, j.next]));
    setDraft((cur) => without(cur, (k) => landed.get(k) === cur[k]));
    setRoleDraft((cur) => without(cur, (k) => savedRoles.includes(k) && cur[k] === roleDraft[k]));
    if (autoSaved) setAutoDraft((cur) => (cur === autoJob ? null : cur));
    setSaving(null);

    if (!failed) setJustSaved(true);
    else if (failed >= total) setError(errMsg(lastError, t('team.savePermissionsFailed')));
    else setError(t('fleet.bulk.someFailed', { count: failed, total }));
  };

  // ---------------------------------------------------------------- render
  if (loading) return <div className="cpt-root"><Loading /></div>;

  const fleetName = agency ? agency.name : t('fleet.personal');
  const fleetRole = agency ? (agency.role ? t(`fleet.role.${agency.role}`) : t('fleet.role.superAdmin')) : null;
  const showPersonal = owned.length + managed.length > 0 || isSuperAdmin;
  const full = !multi && !!selected && hasFullRole(selected);
  const dirtyPeople = new Set([...Object.keys(draft).map((k) => k.split('|')[0]!), ...Object.keys(roleDraft)]);

  const pickablePeople = shownPeople.filter((p) => !hasFullRole(p));
  const allPeoplePicked = pickablePeople.length > 0 && pickablePeople.every((p) => pickedPeople.has(p.user_id));
  const togglePeopleShown = () => setPickedPeople((cur) => {
    const next = new Set(cur);
    pickablePeople.forEach((p) => (allPeoplePicked ? next.delete(p.user_id) : next.add(p.user_id)));
    return next;
  });
  const allAccountsPicked = shownAccounts.length > 0 && shownAccounts.every((a) => pickedAccounts.has(a.account_id));
  const toggleAccountsShown = () => setPickedAccounts((cur) => {
    const next = new Set(cur);
    shownAccounts.forEach((a) => (allAccountsPicked ? next.delete(a.account_id) : next.add(a.account_id)));
    return next;
  });

  /** In bulk mode an account row sums up the ticked people's grants on it. */
  const acrossTargets = (accountId: string): string => {
    if (!targets.every((p) => loaded(p.user_id))) return '…';
    const each = targets.map((p) => accessOf(p.user_id, accountId));
    if (each.every((a) => sameAccess(a, each[0]!))) return summary(each[0]!);
    return t('fleet.bulk.mixed', { k: each.filter((a) => !isEmpty(a)).length, count: each.length });
  };
  /** What the bulk editor starts from: the grants every pair shares, else none. */
  const commonAccess = (): Access => {
    const each = targets.flatMap((p) => accounts.filter((a) => pickedAccounts.has(a.account_id)).map((a) => accessOf(p.user_id, a.account_id)));
    return each.length && each.every((a) => sameAccess(a, each[0]!)) ? { ...each[0]!, fans: [] } : NO_ACCESS;
  };

  return (
    <div className="cpt-root tm-root">
      <div className="tm-frame">
        {/* Header */}
        <div className="tm-head">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
            <span style={{ font: f(700, 17, 1.2), color: C.text }}>{t('fleet.title')}</span>
            <span style={{ font: f(400, 12), color: C.sub }}>{t('fleet.subtitle')}</span>
          </div>
          {isAgency && (myRole === 'owner' || myRole === 'admin') && (
            <label title={t('fleet.autoPlacementTip')} style={{ display: 'flex', alignItems: 'center', gap: 8, font: f(500, 12), color: C.body, cursor: 'pointer' }}>
              <Toggle on={autoPlacement} onChange={stageAutoPlacement} title={t('fleet.autoPlacementTip')} />
              {t('fleet.autoPlacement')}
            </label>
          )}
          {!fleetLoading && (isAgency || accounts.some((a) => a.agency_name)) && (
            <AccountsMenu
              accounts={accounts} byId={byId} isAgency={isAgency} agencyName={agency?.name ?? null}
              pool={pool} anyAccount={isSuperAdmin} busy={placing}
              onAdd={(ids) => void placeAccounts(ids)} onDetach={detach}
            />
          )}
          <FleetSwitcher
            name={fleetName}
            role={fleetRole}
            showPersonal={showPersonal}
            personalMeta={t('fleet.accountCount', { count: isSuperAdmin ? visible.length : owned.length + managed.length })}
            current={fleet}
            agencies={agencies}
            canDelete={!!agency && canGovern}
            avatarUrl={agency?.avatar_url ?? null}
            canEditPicture={!!agency && (canGovern || myRole === 'admin')}
            onPick={switchFleet}
            onCreate={createAgency}
            onDelete={deleteAgency}
            onPicture={(file) => void setAgencyPicture(file)}
          />
        </div>

        {error && <ErrorLine>{error}</ErrorLine>}

        {fleetLoading ? <Loading /> : (
          <div className="tm-grid">
            {/* Members */}
            <div className="tm-card" style={{ display: 'flex', flexDirection: 'column' }}>
              <AddPerson canAdmin={isAgency && canGovern} isAgency={isAgency} count={roster.length} onAdd={addPerson} />
              {roster.length > 0 && (
                <div className="tm-toolbar">
                  {pickablePeople.length > 1 && (
                    <PickBox on={allPeoplePicked} onToggle={togglePeopleShown} title={allPeoplePicked ? t('fleet.clearSelection') : t('fleet.selectAllShown')} />
                  )}
                  <SearchInput value={peopleQuery} onChange={setPeopleQuery} placeholder={t('fleet.searchPeople')} style={{ flex: 1 }} />
                </div>
              )}
              <div style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {!roster.length && <span style={{ padding: 8, font: f(400, 12), color: C.muted }}>{t('team.noMembersHint')}</span>}
                {roster.length > 0 && !shownPeople.length && <span style={{ padding: 8, font: f(400, 12), color: C.muted }}>{t('fleet.noMatches', { q: peopleQuery.trim() })}</span>}
                {shownPeople.map((p) => {
                  const isFull = hasFullRole(p);
                  const sub = isFull ? t('fleet.allAccounts')
                    : unsaved?.user_id === p.user_id ? t('team.unsaved')
                    : t('fleet.kOfN', { k: p.count, count: accounts.length });
                  const on = multi ? pickedPeople.has(p.user_id) : selected?.user_id === p.user_id;
                  return (
                    <div key={p.user_id} className="cpt-hover-row tm-person" style={{ background: on ? 'var(--om-legacy-active)' : 'transparent' }}>
                      <PickBox on={pickedPeople.has(p.user_id)} disabled={isFull}
                        title={isFull ? t('fleet.bulk.fullRoleTip') : t('fleet.bulk.pickPersonTip')}
                        onToggle={() => setPickedPeople((cur) => toggled(cur, p.user_id))} />
                      <button type="button" onClick={() => openPerson(p.user_id)} className="tm-person-open">
                        <Avatar text={initial(nameOf(p))} size={28} round />
                        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <span className="tm-ellipsis" style={{ font: f(500, 13), color: C.text }}>
                            {dirtyPeople.has(p.user_id) && <span className="tm-dirty-dot" title={t('fleet.save.unsavedTip')} />}
                            {nameOf(p)}{p.user_id === myId ? ` · ${t('fleet.you')}` : ''}
                          </span>
                          <span style={{ font: f(400, 11.5), color: C.muted }}>{sub}</span>
                        </span>
                        {isAgency && <RolePill role={p.role ?? null} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Selected person(s) */}
            <div className="tm-card" style={{ display: 'flex', flexDirection: 'column' }}>
              {!targets.length ? (
                <div style={{ padding: 16, font: f(400, 12.5), color: C.muted }}>{t('team.selectMemberHint')}</div>
              ) : (
                <>
                  {multi ? (
                    <div className="tm-person-head">
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ font: f(600, 14), color: C.text }}>{t('fleet.bulk.peopleSelected', { count: targets.length })}</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {targets.map((p) => (
                            <span key={p.user_id} className="tm-chip-sm">
                              <Avatar text={initial(nameOf(p))} size={18} round />
                              <span className="tm-ellipsis" style={{ maxWidth: 160 }}>{nameOf(p)}</span>
                              <button type="button" className="cpt-hover-icon tm-x" style={{ width: 18, height: 18 }} title={t('fleet.bulk.unpick')}
                                onClick={() => setPickedPeople((cur) => toggled(cur, p.user_id))}><Icon name="x" size={10} /></button>
                            </span>
                          ))}
                        </div>
                      </div>
                      <Btn kind="muted" onClick={() => setPickedPeople(new Set())}>{t('fleet.clearSelection')}</Btn>
                    </div>
                  ) : selected && (
                    <div className="tm-person-head">
                      <Avatar text={initial(nameOf(selected))} size={36} round />
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span className="tm-ellipsis" style={{ font: f(600, 14), color: C.text }}>{nameOf(selected)}</span>
                        <span className="tm-ellipsis" style={{ font: f(400, 12), color: C.sub }}>{selected.email}</span>
                      </div>
                      <PersonControls
                        person={selected} isAgency={isAgency} myId={myId} myRole={myRole} canGovern={canGovern}
                        onRole={(r) => stageRole(selected, r)} onMakeMember={() => void makeMember(selected)}
                        onMakeOwner={() => makeOwner(selected)} onRemove={() => removePerson(selected)}
                      />
                    </div>
                  )}
                  {full ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 14px' }}>
                      <div style={{ width: 28, height: 28, flex: 'none', borderRadius: 7, background: C.tint16, color: C.purpleLt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="shield" size={14} /></div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span style={{ font: f(500, 13), color: C.text }}>{t('fleet.fullAccess', { count: accounts.length })}</span>
                        <span style={{ font: f(400, 11.5), color: C.sub }}>{selected!.role === 'owner' ? t('fleet.ownerNote') : t('fleet.adminNote')}</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, padding: '12px 14px 6px' }}>
                        <span style={{ font: f(600, 13), color: C.text }}>{t('team.accountAccess')}</span>
                        <span style={{ font: f(400, 11.5), color: C.muted }}>{multi ? t('fleet.bulk.multiHint') : t('fleet.accessHint')}</span>
                      </div>
                      {!accounts.length ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px 14px', font: f(400, 12), color: C.purplePale }}>
                          <Icon name="alert-circle" size={13} />{isAgency ? t('fleet.noAccountsAgency') : t('team.noOwnedAccountsHint')}
                        </div>
                      ) : (
                        <>
                          <div className="tm-toolbar" style={{ borderBottom: 0, padding: '4px 14px 8px' }}>
                            <PickBox on={allAccountsPicked} onToggle={toggleAccountsShown} title={allAccountsPicked ? t('fleet.clearSelection') : t('fleet.selectAllShown')} />
                            <SearchInput value={accountQuery} onChange={setAccountQuery} placeholder={t('fleet.searchAccounts')} style={{ flex: 1, maxWidth: 360 }} />
                            <span style={{ font: f(400, 11.5), color: C.muted, whiteSpace: 'nowrap' }}>
                              {accountQuery.trim() ? t('fleet.kOfN', { k: shownAccounts.length, count: accounts.length }) : t('fleet.accountCount', { count: accounts.length })}
                            </span>
                          </div>
                          {(multi || pickedAccounts.size > 0) && (
                            <BulkBar
                              key={`${targetKey}|${[...pickedAccounts].join(',')}`}
                              people={targets.length} multi={multi} accounts={pickedAccounts.size}
                              ready={targets.every((p) => loaded(p.user_id))}
                              progress={saving} notice={notice} seed={commonAccess}
                              onApply={applyBulk} onClear={() => { setPickedAccounts(new Set()); setNotice(null); }}
                            />
                          )}
                          {!shownAccounts.length && <span style={{ padding: '10px 14px 14px', font: f(400, 12), color: C.muted }}>{t('fleet.noMatches', { q: accountQuery.trim() })}</span>}
                          {shownAccounts.map((a) => {
                            const meta = byId.get(a.account_id);
                            const pick = { on: pickedAccounts.has(a.account_id), onToggle: () => setPickedAccounts((cur) => toggled(cur, a.account_id)), title: t('fleet.bulk.pickAccountTip') };
                            const avatar = <AccountAvatar avatarUrl={meta?.avatar_url} platform={meta?.platform ?? undefined} size={28} />;
                            if (multi) {
                              return <AccessRow key={a.account_id} readOnly avatar={avatar} title={a.name} sub={acrossTargets(a.account_id)} access={NO_ACCESS} onChange={() => {}} pick={pick} />;
                            }
                            const uid = selected!.user_id;
                            return (
                              <AccessRow
                                key={a.account_id}
                                avatar={avatar}
                                title={a.name}
                                sub={loaded(uid) ? undefined : '…'}
                                locked={isAgency && a.owner_id === uid}
                                access={accessOf(uid, a.account_id)}
                                dirty={pairKey(uid, a.account_id) in draft}
                                onChange={(next) => stageAccess(uid, a.account_id, next)}
                                pick={pick}
                              />
                            );
                          })}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {!fleetLoading && (
          <SaveBar count={dirtyCount} progress={saving} saved={justSaved} onSave={() => void save()} onDiscard={discard} />
        )}
      </div>
    </div>
  );
}

/* ── pieces ─────────────────────────────────────────────────────── */

/** Nothing on this page is written until Save: the draft count, Discard and Save. */
export function SaveBar({ count, progress, saved, onSave, onDiscard }: {
  count: number; progress: { done: number; total: number } | null; saved: boolean; onSave: () => void; onDiscard: () => void;
}) {
  const { t } = useTranslation('agency');
  const busy = !!progress;
  return (
    <div className={`tm-savebar${count > 0 || busy ? ' is-dirty' : ''}`} role="region" aria-label={t('fleet.save.save')}>
      <Icon name={count > 0 ? 'alert-circle' : 'check'} size={14} color={count > 0 ? C.purpleLt : C.muted} />
      <span style={{ flex: 1, minWidth: 0, font: f(500, 12.5), color: count > 0 ? C.text : C.muted }}>
        {busy ? t('fleet.bulk.applying', { done: progress!.done, total: progress!.total })
          : count > 0 ? t('fleet.save.unsavedCount', { count }) : saved ? t('fleet.save.saved') : t('fleet.save.allSaved')}
      </span>
      {busy && <Spin size={11} />}
      <Btn kind="muted" disabled={!count || busy} onClick={onDiscard}>{t('fleet.save.discard')}</Btn>
      <Btn kind="primary" icon="save" disabled={!count || busy} onClick={onSave}>{t('fleet.save.save')}</Btn>
    </div>
  );
}

/** Set the same access on every ticked account (for every person being edited). */
function BulkBar({ people, multi, accounts, ready, progress, notice, seed, onApply, onClear }: {
  people: number; multi: boolean; accounts: number; ready: boolean;
  progress: { done: number; total: number } | null; notice: string | null;
  seed: () => Access; onApply: (template: Access) => void; onClear: () => void;
}) {
  const { t } = useTranslation('agency');
  const [custom, setCustom] = useState<Access | null>(null);
  const busy = !!progress;
  const disabled = busy || !ready || accounts === 0;
  const pick = (id: PresetId) => { setCustom(null); onApply(applyPreset(id)); };
  return (
    <div className="tm-bulk">
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 10px' }}>
        <Icon name="layers" size={14} color={C.purpleLt} />
        <span style={{ flex: '1 1 200px', minWidth: 0, font: f(500, 12.5), color: C.text }}>
          {accounts === 0
            ? t('fleet.bulk.pickHint')
            : multi
              ? t('fleet.bulk.forPeopleOnAccounts', { people: t('fleet.bulk.people', { count: people }), accounts: t('fleet.accountCount', { count: accounts }) })
              : t('fleet.bulk.accountsSelected', { count: accounts })}
        </span>
        {busy ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, font: f(500, 12), color: C.purplePale }}>
            <Spin size={11} />{t('fleet.bulk.applying', { done: progress!.done, total: progress!.total })}
          </span>
        ) : (
          <>
            <span style={{ font: f(400, 12), color: C.sub }}>{t('fleet.bulk.setTo')}</span>
            {PRESETS.map((p) => (
              <Btn key={p.id} kind="ghost" disabled={disabled} title={t(p.tipKey)} onClick={() => pick(p.id)}>{t(p.labelKey)}</Btn>
            ))}
            <Btn kind={custom ? 'tint' : 'ghost'} icon="sliders" disabled={disabled} onClick={() => setCustom(custom ? null : seed())}>{t('fleet.bulk.custom')}</Btn>
            {accounts > 0 && <Btn kind="text" onClick={onClear}>{t('fleet.clearSelection')}</Btn>}
          </>
        )}
      </div>
      {notice && !busy && <span style={{ display: 'flex', alignItems: 'center', gap: 6, font: f(500, 12), color: C.purplePale }}><Icon name="check" size={12} />{notice}</span>}
      {custom && !busy && (
        <div className="tm-bulk-editor">
          <AccessEditor access={custom} onChange={setCustom} fans={false} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', padding: '0 14px 12px' }}>
            <span style={{ flex: 1, font: f(400, 11.5), color: C.muted }}>{t('fleet.bulk.fansKept')}</span>
            <Btn kind="muted" onClick={() => setCustom(null)}>{t('fleet.bulk.cancel')}</Btn>
            <Btn kind="primary" disabled={disabled} onClick={() => onApply(custom)}>{t('fleet.bulk.apply')}</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

function RolePill({ role }: { role: AgencyRole | null }) {
  const { t } = useTranslation('agency');
  if (role === 'member') return null;
  return (
    <span className="tm-role" style={{ color: role ? C.purpleLt : C.muted }} title={role ? undefined : t('fleet.externalTip')}>
      {role ? t(`fleet.role.${role}`) : t('fleet.external')}
    </span>
  );
}

const MENU_GUTTER = 8;

export function Menu({ open, onClose, children, width, align = 'left' }: { open: boolean; onClose: () => void; children: ReactNode; width: number; align?: 'left' | 'right' }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shift, setShift] = useState(0);
  // On a phone the anchored edge can push the menu off-screen; nudge it back inside the viewport.
  useLayoutEffect(() => {
    if (!open || !ref.current) { setShift(0); return; }
    const r = ref.current.getBoundingClientRect();
    const left = r.left - shift, right = r.right - shift;
    const vw = document.documentElement.clientWidth;
    let dx = 0;
    if (left < MENU_GUTTER) dx = MENU_GUTTER - left;
    else if (right > vw - MENU_GUTTER) dx = Math.max(MENU_GUTTER - left, vw - MENU_GUTTER - right);
    if (dx !== shift) setShift(dx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, width, align]);
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 5 }} />
      <div ref={ref} className="tm-menu" style={{ width, maxWidth: `calc(100vw - ${MENU_GUTTER * 2}px)`, [align]: 0, transform: shift ? `translateX(${shift}px)` : undefined }}>{children}</div>
    </>
  );
}

function FleetSwitcher({ name, role, showPersonal, personalMeta, current, agencies, canDelete, avatarUrl, canEditPicture, onPick, onCreate, onDelete, onPicture }: {
  name: string; role: string | null; showPersonal: boolean; personalMeta: string; current: string | null; agencies: AgencySummary[];
  canDelete: boolean; avatarUrl: string | null; canEditPicture: boolean;
  onPick: (id: string) => void; onCreate: (name: string) => void; onDelete: () => void; onPicture: (file: File | null) => void;
}) {
  const { t } = useTranslation('agency');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const pick = (id: string) => { setOpen(false); onPick(id); };
  const create = () => { const v = draft.trim(); if (!v) return; setDraft(''); setOpen(false); onCreate(v); };
  const row = (id: string, label: string, meta: string, r: string | null, src?: string | null) => (
    <button key={id} type="button" onClick={() => pick(id)} className="cpt-hover-row tm-menu-row" style={{ background: current === id ? 'var(--om-legacy-active)' : 'transparent' }}>
      <Avatar text={initial(label)} size={24} src={src} />
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <span className="tm-ellipsis" style={{ font: f(500, 13), color: C.text }}>{label}</span>
        <span style={{ font: f(400, 11), color: C.muted }}>{meta}</span>
      </span>
      {r && <span className="tm-role" style={{ color: C.purpleLt }}>{r}</span>}
      <span style={{ width: 13, color: C.purpleLt }}>{current === id && <Icon name="check" size={13} />}</span>
    </button>
  );
  return (
    <div style={{ position: 'relative', flex: 'none' }}>
      <button type="button" onClick={() => setOpen(!open)} className="cpt-hover-border tm-switch">
        <Avatar text={initial(name)} size={24} src={avatarUrl} />
        <span style={{ font: f(600, 13), color: C.text }}>{name}</span>
        {role && <span className="tm-role" style={{ color: C.purpleLt }}>{role}</span>}
        <Icon name="chevron-down" size={13} color={C.sub} />
      </button>
      <Menu open={open} onClose={() => setOpen(false)} width={300} align="right">
        {showPersonal && (
          <div style={{ padding: 6, borderBottom: `1px solid ${C.divider}` }}>
            {row(PERSONAL, t('fleet.personal'), personalMeta, null)}
          </div>
        )}
        <span className="tm-menu-label">{t('fleet.yourAgencies')}</span>
        <div style={{ padding: '0 6px 6px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!agencies.length && <span style={{ padding: '4px 8px', font: f(400, 12), color: C.muted }}>{t('fleet.noAgencies')}</span>}
          {agencies.map((a) => row(a.id, a.name,
            `${t('fleet.memberCount', { count: a.member_count })} · ${t('fleet.accountCount', { count: a.account_count })}`,
            a.role ? t(`fleet.role.${a.role}`) : null, a.avatar_url))}
        </div>
        <div style={{ display: 'flex', gap: 6, padding: 8, borderTop: `1px solid ${C.divider}` }}>
          <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
            placeholder={t('fleet.newAgencyPlaceholder')} className="tm-input" style={{ flex: 1, height: 28 }} />
          <Btn kind="primary" onClick={create} disabled={!draft.trim()}>{t('fleet.create')}</Btn>
        </div>
        {(canEditPicture || canDelete) && (
          <div style={{ padding: '4px 6px 6px', borderTop: `1px solid ${C.divider}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {canEditPicture && (
              <button type="button" onClick={() => { setOpen(false); fileInput.current?.click(); }} className="cpt-hover-row tm-menu-row" style={{ color: C.sub, font: f(500, 12), height: 28 }}>
                <Icon name="image" size={12} />{avatarUrl ? t('fleet.changePicture') : t('fleet.uploadPicture')}
              </button>
            )}
            {canEditPicture && avatarUrl && (
              <button type="button" onClick={() => { setOpen(false); onPicture(null); }} className="cpt-hover-row tm-menu-row" style={{ color: C.sub, font: f(500, 12), height: 28 }}>
                <Icon name="x" size={12} />{t('fleet.removePicture')}
              </button>
            )}
            {canDelete && (
              <button type="button" onClick={() => { setOpen(false); onDelete(); }} className="cpt-hover-row tm-menu-row" style={{ color: C.sub, font: f(500, 12), height: 28 }}>
                <Icon name="trash-2" size={12} />{t('fleet.deleteAgency', { name })}
              </button>
            )}
          </div>
        )}
      </Menu>
      <input ref={fileInput} type="file" accept={AVATAR_ACCEPT} hidden
        onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) onPicture(file); }} />
    </div>
  );
}

/** The fleet's accounts behind one header button: an agency adds and removes
    accounts here; a Personal fleet sees which of its accounts sit in an agency. */
export function AccountsMenu({ accounts, byId, isAgency, agencyName, pool, anyAccount, busy, onAdd, onDetach }: {
  accounts: FleetAccount[]; byId: Map<string, Account>; isAgency: boolean; agencyName: string | null;
  pool: PoolAccount[]; anyAccount: boolean; busy: boolean;
  onAdd: (ids: string[]) => void; onDetach: (acc: FleetAccount, where: string) => void;
}) {
  const { t } = useTranslation('agency');
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const close = () => { setOpen(false); setAdding(false); setQuery(''); setPicked(new Set()); };
  const shown = accounts.filter((a) => matches(query, a.name, byId.get(a.account_id)?.username));
  const shownPool = pool.filter((p) => matches(query, p.name, byId.get(p.account_id)?.username));
  const allPicked = shownPool.length > 0 && shownPool.every((p) => picked.has(p.account_id));
  return (
    <div style={{ position: 'relative', flex: 'none' }}>
      <button type="button" onClick={() => (open ? close() : setOpen(true))} className="cpt-hover-border tm-switch">
        {busy ? <Spin size={12} /> : <Icon name="layout-grid" size={14} color={C.purpleLt} />}
        <span style={{ font: f(600, 13), color: C.text }}>{t('fleet.accounts')}</span>
        <span style={{ font: f(500, 12), color: C.muted }}>{accounts.length}</span>
        <Icon name="chevron-down" size={13} color={C.sub} />
      </button>
      <Menu open={open} onClose={close} width={320} align="right">
        <div style={{ padding: 8, borderBottom: `1px solid ${C.divider}`, display: 'flex', alignItems: 'center', gap: 6 }}>
          {adding && (
            <button type="button" className="cpt-hover-icon tm-x" title={t('fleet.bulk.back')} onClick={() => { setAdding(false); setQuery(''); setPicked(new Set()); }}>
              <Icon name="chevron-left" size={14} />
            </button>
          )}
          <SearchInput value={query} onChange={setQuery} placeholder={t('fleet.searchAccounts')} style={{ flex: 1 }} />
        </div>
        <div className="cpt-thin-scroll" style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 380, overflowY: 'auto' }}>
          {!adding ? (
            <>
              {shown.map((a) => {
                const meta = byId.get(a.account_id);
                return (
                  <div key={a.account_id} className="tm-menu-row" style={{ cursor: 'default' }}>
                    <AccountAvatar avatarUrl={meta?.avatar_url} platform={meta?.platform ?? undefined} size={22} />
                    <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <span className="tm-ellipsis" style={{ font: f(500, 12.5), color: C.text }}>{a.name}</span>
                      {!isAgency && a.agency_name && <span style={{ font: f(400, 11), color: C.muted }}>{t('fleet.inAgencyTip', { agency: a.agency_name })}</span>}
                    </span>
                    {(isAgency || a.agency_name) && (
                      <button type="button" title={isAgency ? t('fleet.detach') : t('fleet.detachFrom', { agency: a.agency_name })}
                        onClick={() => onDetach(a, isAgency ? agencyName! : a.agency_name!)} className="cpt-hover-icon tm-x">
                        <Icon name="x" size={12} />
                      </button>
                    )}
                  </div>
                );
              })}
              {!accounts.length && <span style={{ padding: 6, font: f(400, 12), color: C.muted }}>{isAgency ? t('fleet.noAccountsAgency') : t('team.noOwnedAccountsHint')}</span>}
              {accounts.length > 0 && !shown.length && <span style={{ padding: 6, font: f(400, 12), color: C.muted }}>{t('fleet.noMatches', { q: query.trim() })}</span>}
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px' }}>
                <span className="tm-menu-label" style={{ padding: 0 }}>{anyAccount ? t('fleet.allAccountsLabel') : t('fleet.yourAccounts')}</span>
                {shownPool.length > 1 && (
                  <button type="button" onClick={() => setPicked((cur) => {
                    const next = new Set(cur);
                    shownPool.forEach((p) => (allPicked ? next.delete(p.account_id) : next.add(p.account_id)));
                    return next;
                  })}
                    style={{ padding: 0, border: 0, background: 'transparent', cursor: 'pointer', font: f(500, 11), color: C.purpleLt }}>
                    {allPicked ? t('fleet.clearSelection') : t('fleet.selectAll')}
                  </button>
                )}
              </div>
              {shownPool.map((p) => (
                <button key={p.account_id} type="button" onClick={() => setPicked((cur) => toggled(cur, p.account_id))} className="cpt-hover-row tm-menu-row">
                  <Check state={picked.has(p.account_id) ? 'all' : 'none'} />
                  <AccountAvatar avatarUrl={byId.get(p.account_id)?.avatar_url} platform={byId.get(p.account_id)?.platform ?? undefined} size={22} />
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <span className="tm-ellipsis" style={{ font: f(500, 12.5), color: p.pending ? C.sub : C.text }}>{p.name}</span>
                    {p.pending && <span style={{ font: f(400, 11), color: C.muted }}>{t('fleet.notConnected')}</span>}
                    {!p.pending && p.agency_name && <span style={{ font: f(400, 11), color: C.muted }}>{t('fleet.movesFrom', { agency: p.agency_name })}</span>}
                  </span>
                </button>
              ))}
              {!pool.length && <span style={{ padding: 6, font: f(400, 12), color: C.muted }}>{t('fleet.poolEmpty')}</span>}
              {pool.length > 0 && !shownPool.length && <span style={{ padding: 6, font: f(400, 12), color: C.muted }}>{t('fleet.noMatches', { q: query.trim() })}</span>}
              <span style={{ padding: '6px 6px 2px', font: f(400, 11), color: C.muted }}>{anyAccount ? t('fleet.addAccountHintSuper') : t('fleet.addAccountHint')}</span>
            </>
          )}
        </div>
        {isAgency && !adding && (
          <div style={{ padding: 8, borderTop: `1px solid ${C.border}`, display: 'flex' }}>
            <Btn kind="tint" icon="plus" onClick={() => { setAdding(true); setQuery(''); }}>{t('fleet.addAccount')}</Btn>
          </div>
        )}
        {adding && pool.length > 0 && (
          <div style={{ padding: 8, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end' }}>
            <Btn kind="primary" disabled={!picked.size || busy} onClick={() => { const ids = pool.filter((p) => picked.has(p.account_id)).map((p) => p.account_id); close(); onAdd(ids); }}>
              {picked.size ? t('fleet.addSelected', { count: picked.size }) : t('fleet.add')}
            </Btn>
          </div>
        )}
      </Menu>
    </div>
  );
}

function AddPerson({ canAdmin, isAgency, count, onAdd }: { canAdmin: boolean; isAgency: boolean; count: number; onAdd: (identifier: string, role: AgencyRole) => Promise<void> }) {
  const { t } = useTranslation('agency');
  const [value, setValue] = useState('');
  const [role, setRole] = useState<AgencyRole>('member');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    const v = value.trim();
    if (!v || busy) return;
    setBusy(true);
    await onAdd(v, role);
    setBusy(false);
    setValue('');
  };
  return (
    <div style={{ padding: '12px 12px 10px', display: 'flex', flexDirection: 'column', gap: 8, borderBottom: `1px solid ${C.divider}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ font: f(600, 13), color: C.text }}>{t('fleet.members')}</span>
        <span style={{ font: f(400, 11.5), color: C.muted }}>{t('fleet.memberCount', { count })}</span>
      </div>
      <input value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
        placeholder={t('team.addPlaceholder')} title={t('team.addHint')} className="tm-input" style={{ height: 30 }} />
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {isAgency && (
          <Seg<AgencyRole>
            value={role}
            onChange={setRole}
            options={[
              { id: 'member', label: t('fleet.role.member'), title: t('fleet.memberTip') },
              ...(canAdmin ? [{ id: 'admin' as AgencyRole, label: t('fleet.role.admin'), title: t('fleet.adminTip') }] : []),
            ]}
          />
        )}
        <div style={{ flex: 1 }} />
        <Btn kind="primary" onClick={() => void add()} disabled={!value.trim() || busy}>{busy ? <Spin size={11} /> : t('team.add')}</Btn>
      </div>
    </div>
  );
}

function PersonControls({ person, isAgency, myId, myRole, canGovern, onRole, onMakeMember, onMakeOwner, onRemove }: {
  person: Person; isAgency: boolean; myId: string; myRole: AgencyRole | null; canGovern: boolean;
  onRole: (r: AgencyRole) => void; onMakeMember: () => void; onMakeOwner: () => void; onRemove: () => void;
}) {
  const { t } = useTranslation('agency');
  const remove = <Btn kind="muted" icon="user-minus" onClick={onRemove}>{t('fleet.remove')}</Btn>;
  if (!isAgency) return person.user_id === myId ? null : remove;
  if (person.role === 'owner') {
    return <span className="tm-owner-pill"><Icon name="award" size={12} />{t('fleet.role.owner')}</span>;
  }
  const isCoManager = myRole === 'owner' || myRole === 'admin' || canGovern;
  // Only the agency owner touches the admin tier; admins manage plain members and Externals.
  const canTouch = canGovern || (isCoManager && person.role !== 'admin');
  if (person.role == null) {
    return (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Btn kind="tint" icon="user-plus" onClick={onMakeMember}>{t('fleet.makeMember')}</Btn>
        {canTouch && remove}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
      {canGovern && person.user_id !== myId && <Btn kind="text" onClick={onMakeOwner}>{t('fleet.makeOwner')}</Btn>}
      <Seg<AgencyRole>
        value={person.role}
        onChange={onRole}
        disabled={!canGovern}
        options={[
          { id: 'admin', label: t('fleet.role.admin'), title: t('fleet.adminTip') },
          { id: 'member', label: t('fleet.role.member'), title: t('fleet.memberTip') },
        ]}
      />
      {canTouch && person.user_id !== myId && remove}
    </div>
  );
}
